import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Lock, MessageSquare, ShieldCheck } from 'lucide-react'
import { PhoneInput, phoneIsValid, DEFAULT_COUNTRY } from '@/components/erp/phone-input'
import type { Country } from 'react-phone-number-input'
import { config } from '@/config/runtime-config'
import { api } from '@/shared/api/client'
import { explain } from '@/shared/api/problem'

/**
 * "I forgot my password" — the console's only way back in.
 *
 * ## Why this page had to exist
 *
 * There was no recovery on the web at all. `/auth/password/change` proves the
 * current password, and an administrator-initiated reset needs an administrator
 * already signed in. On a campaign whose console accounts belong to candidates
 * and coordinators rather than staff with an IT desk, "ask an administrator" is
 * not a recovery — and the first administrator has nobody to ask.
 *
 * ## A code, not a link
 *
 * The code arrives by SMS because the phone number *is* the account identity
 * here; e-mail is an optional contact field many accounts do not have.
 *
 * It is a code rather than a reset link because of which device the person is
 * on. A link in an SMS opens the browser on the PHONE, and this is a console
 * password — they are at a laptop, which is where the new password has to be set
 * and where they will sign in. A code crosses that gap: asked for on the laptop,
 * read off the phone, typed on the laptop.
 *
 * ## Both steps live on one page, deliberately
 *
 * Two routes would mean carrying the phone number between them in a URL, which
 * puts an account identifier in browser history and in any referrer. Holding it
 * in component state costs nothing and keeps it out of both. The server is
 * stateless across the two calls — the number is re-sent and the code names its
 * own challenge — so nothing is lost by this.
 *
 * ## What it must never say
 *
 * Whether the number has an account. The server already answers identically for
 * a registered and an unregistered number, down to the timing, so this page must
 * not undo that by rendering a different screen for the two cases. It advances to
 * step two either way, and the confirmation is phrased as "if that number has an
 * account" rather than "we sent you a code".
 */
export function ForgotPasswordPage() {
  const [step, setStep] = useState<'phone' | 'code'>('phone')

  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [code, setCode] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const [maskedPhone, setMaskedPhone] = useState('')
  // Counted down so the person can see the code die rather than discovering it
  // by being refused.
  const [secondsLeft, setSecondsLeft] = useState(0)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => { document.title = `Reset your password — ${config.appName}` }, [])

  // Carried over from the sign-in page, so somebody who has already typed their
  // number once does not type it again.
  useEffect(() => {
    const p = searchParams.get('phone')
    if (p) setPhone(p)
  }, [searchParams])

  useEffect(() => {
    if (secondsLeft <= 0) return
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [secondsLeft])

  const mismatch = confirm.length > 0 && next !== confirm
  const codeComplete = /^[0-9]{6}$/.test(code)

  async function requestCode(e?: FormEvent) {
    e?.preventDefault()
    setError('')
    if (!phone) { setError('Phone number is required'); return }
    // Caught here rather than by a 400: an unparseable number and an unknown
    // account are different mistakes, and only the first one can be told apart
    // without leaking whether the account exists.
    if (!phoneIsValid(phone)) { setError('That is not a valid phone number'); return }
    setBusy(true)
    try {
      const res = await api.post<{ maskedPhone: string; expiresInSeconds: number }>(
        '/auth/password/reset/start', { phone }, { anonymous: true },
      )
      setMaskedPhone(res.maskedPhone)
      setSecondsLeft(res.expiresInSeconds)
      setStep('code')
      // Cleared on a resend so a stale code is never left sitting in the box.
      setCode('')
    } catch (err) {
      setError(explain(err) || 'Could not send a code. Try again shortly.')
    } finally { setBusy(false) }
  }

  async function submitNewPassword(e: FormEvent) {
    e.preventDefault()
    if (mismatch) return
    setError('')
    setBusy(true)
    try {
      await api.post(
        '/auth/password/reset/complete',
        { phone, code, newPassword: next },
        { anonymous: true },
      )
      setDone(true)
      // Straight to sign-in with the number prefilled. Not signed in
      // automatically: the reset proves the SIM, and a session is worth more
      // than that — the new password should be used once, deliberately.
      setTimeout(() => navigate(`/login?phone=${encodeURIComponent(phone)}`, { replace: true }), 1400)
    } catch (err) {
      setError(explain(err) || 'That code is not valid. Ask for a new one.')
    } finally { setBusy(false) }
  }

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="erp-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-lg bg-chocolate/10 border border-chocolate/15 inline-flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-chocolate" />
            </div>
            <div>
              <h1 className="text-erp-lg font-bold tracking-tight m-0">Reset your password</h1>
              <p className="text-erp-xs text-muted-foreground m-0">{config.appName}</p>
            </div>
          </div>

          {done ? (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-erp-sm text-emerald-900">
              <b>Password changed.</b>
              <div className="mt-0.5 text-emerald-800">
                Every other session on this account has been signed out. Taking you to sign-in…
              </div>
            </div>
          ) : step === 'phone' ? (
            <form onSubmit={requestCode} className="space-y-4">
              <p className="text-erp-sm text-muted-foreground m-0">
                Enter the phone number your account uses. We will text you a six-digit
                code.
              </p>
              <div>
                <label htmlFor="fp-phone" className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">
                  Phone number
                </label>
                <PhoneInput
                  id="fp-phone" value={phone} onChange={setPhone}
                  country={country} onCountryChange={setCountry}
                  disabled={busy} autoFocus
                />
              </div>

              {error && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-md text-erp-sm text-rose-700">{error}</div>
              )}

              <button
                type="submit" disabled={busy || !phone}
                className="w-full h-11 inline-flex items-center justify-center text-erp-sm bg-chocolate text-chocolate-foreground rounded-md hover:bg-chocolate-muted disabled:opacity-50 font-medium transition-colors"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Text me a code
              </button>
            </form>
          ) : (
            <form onSubmit={submitNewPassword} className="space-y-4">
              <div className="flex items-start gap-2.5 p-3 bg-muted/40 border border-border rounded-md">
                <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-erp-sm text-muted-foreground">
                  {/* "IF that number has an account" — never "we sent you a code".
                      The server answers a registered and an unregistered number
                      identically, and this sentence is where a careless rewrite
                      would give that away. */}
                  If {maskedPhone} has an account, a six-digit code is on its way to it.
                  {secondsLeft > 0 && (
                    <> It expires in <span className="font-mono tabular-nums">{mmss}</span>.</>
                  )}
                  {secondsLeft <= 0 && <> That code has expired — ask for a new one.</>}
                </div>
              </div>

              <div>
                <label htmlFor="fp-code" className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">
                  Code from the SMS
                </label>
                <input
                  id="fp-code" inputMode="numeric" autoComplete="one-time-code"
                  // The server takes exactly six digits; stripping non-digits here
                  // means a pasted code with a stray space is not a refusal.
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" autoFocus
                  className="w-full h-11 px-3 text-erp-md font-mono tracking-[0.4em] text-center bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                />
              </div>

              {[
                { id: 'fp-new', label: 'New password', v: next, set: setNext },
                { id: 'fp-cf', label: 'Confirm new password', v: confirm, set: setConfirm },
              ].map((f) => (
                <div key={f.id}>
                  <label htmlFor={f.id} className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">{f.label}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
                    <input
                      id={f.id} type="password" required autoComplete="new-password"
                      value={f.v} onChange={(e) => f.set(e.target.value)}
                      className="w-full h-11 pl-10 pr-3 text-erp-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                    />
                  </div>
                </div>
              ))}

              {mismatch && <div className="text-erp-sm text-red-700">Passwords do not match.</div>}
              {error && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-md text-erp-sm text-rose-700">{error}</div>
              )}

              <button
                type="submit" disabled={busy || mismatch || !codeComplete || !next || !confirm}
                className="w-full h-11 inline-flex items-center justify-center text-erp-sm bg-chocolate text-chocolate-foreground rounded-md hover:bg-chocolate-muted disabled:opacity-50 font-medium transition-colors"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Set new password
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button" onClick={() => { setStep('phone'); setError('') }}
                  className="text-erp-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Use a different number
                </button>
                <button
                  type="button" onClick={() => requestCode()} disabled={busy}
                  className="erp-link text-erp-xs disabled:opacity-50"
                >
                  Send a new code
                </button>
              </div>

              <p className="text-erp-xs text-muted-foreground m-0">
                Setting a new password signs out every session on this account.
              </p>
            </form>
          )}

          {!done && (
            <div className="mt-5 pt-4 border-t border-border">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </Link>
            </div>
          )}
        </div>

        <p className="mt-3 text-center text-erp-xs text-muted-foreground">
          Stuck? Contact{' '}
          <a href={`mailto:${config.login.supportEmail}`} className="erp-link">{config.login.supportEmail}</a>.
        </p>
      </div>
    </div>
  )
}
